import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES } from '@/constants/apiRoutes'
import {
  BulkNotificationExecuteRequest,
  BulkNotificationExecuteResponse,
  BulkNotificationState,
  ScheduleCreateRequest,
  ScheduleCreateResponse,
  ScheduleListResponse,
  ScheduleActionResponse,
} from '@/types/bulkNotification'

export const executeBulkNotification = createAsyncThunk<
  BulkNotificationExecuteResponse,
  BulkNotificationExecuteRequest
>(API_ROUTES.BULK_NOTIFICATION_EXECUTE_ENDPOINT, async (payload) => {
  const res = await apiClient.post<BulkNotificationExecuteResponse>(
    API_ROUTES.BULK_NOTIFICATION_EXECUTE_ENDPOINT,
    payload,
  )
  return res
})

export const fetchSchedules = createAsyncThunk<ScheduleListResponse>(
  'bulkNotification/fetchSchedules',
  async () => {
    return await apiClient.get<ScheduleListResponse>(API_ROUTES.BULK_NOTIFICATION_SCHEDULES_LIST)
  }
)

export const createSchedule = createAsyncThunk<ScheduleCreateResponse, ScheduleCreateRequest>(
  'bulkNotification/createSchedule',
  async (payload) => {
    return await apiClient.post<ScheduleCreateResponse>(
      API_ROUTES.BULK_NOTIFICATION_SCHEDULE_CREATE,
      payload,
    )
  }
)

export const stopSchedule = createAsyncThunk<ScheduleActionResponse, number>(
  'bulkNotification/stopSchedule',
  async (id) => {
    return await apiClient.patch<ScheduleActionResponse>(
      API_ROUTES.BULK_NOTIFICATION_SCHEDULE_STOP(id),
      {},
    )
  }
)

export const resumeSchedule = createAsyncThunk<ScheduleActionResponse, number>(
  'bulkNotification/resumeSchedule',
  async (id) => {
    return await apiClient.patch<ScheduleActionResponse>(
      API_ROUTES.BULK_NOTIFICATION_SCHEDULE_RESUME(id),
      {},
    )
  }
)

export const updateSchedule = createAsyncThunk<
  ScheduleActionResponse,
  { id: number; payload: ScheduleCreateRequest }
>(
  'bulkNotification/updateSchedule',
  async ({ id, payload }) => {
    return await apiClient.patch<ScheduleActionResponse>(
      API_ROUTES.BULK_NOTIFICATION_SCHEDULE_UPDATE(id),
      payload,
    )
  }
)

export const deleteSchedule = createAsyncThunk<ScheduleActionResponse, number>(
  'bulkNotification/deleteSchedule',
  async (id) => {
    return await apiClient.delete<ScheduleActionResponse>(
      API_ROUTES.BULK_NOTIFICATION_SCHEDULE_DELETE(id),
    )
  }
)

const initialState: BulkNotificationState = {
  loading: false,
  error: null,
  lastResult: null,
  schedules: [],
  schedulesLoading: false,
  scheduleActionLoading: false,
  lastRunAt: null,
  elapsedMinutes: null,
}

const bulkNotificationSlice = createSlice({
  name: 'bulkNotification',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // --- executeBulkNotification ---
    builder
      .addCase(executeBulkNotification.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(executeBulkNotification.fulfilled, (state, action) => {
        state.loading = false
        state.lastResult = action.payload
      })
      .addCase(executeBulkNotification.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Error'
      })

    // --- fetchSchedules ---
    builder
      .addCase(fetchSchedules.pending, (state) => {
        state.schedulesLoading = true
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.schedulesLoading = false
        state.schedules = action.payload.schedules
        state.lastRunAt = action.payload.last_run_at
        state.elapsedMinutes = action.payload.elapsed_minutes
      })
      .addCase(fetchSchedules.rejected, (state) => {
        state.schedulesLoading = false
      })

    // --- createSchedule ---
    builder
      .addCase(createSchedule.pending, (state) => {
        state.scheduleActionLoading = true
      })
      .addCase(createSchedule.fulfilled, (state) => {
        state.scheduleActionLoading = false
      })
      .addCase(createSchedule.rejected, (state) => {
        state.scheduleActionLoading = false
      })

    // --- stopSchedule / resumeSchedule / updateSchedule / deleteSchedule ---
    const actionThunks = [stopSchedule, resumeSchedule, updateSchedule, deleteSchedule]
    actionThunks.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.scheduleActionLoading = true
        })
        .addCase(thunk.fulfilled, (state) => {
          state.scheduleActionLoading = false
        })
        .addCase(thunk.rejected, (state) => {
          state.scheduleActionLoading = false
        })
    })
  },
})

export default bulkNotificationSlice.reducer
